import { Button, Theme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import { Formik } from 'formik';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import cookie from 'react-cookies';
import { useMutation } from 'react-query';
import { Redirect } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import {
  EEventGroup,
  EEventType,
  ELocation,
  useCreateEventMutation,
} from '../../../api/gqlQuery';
import { FormikTextField } from '../../../common/formik-material-ui';
import { usePrevious } from '../../../common/usePrevious';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { ModalProgressBar } from '../../../_metronic/_partials/controls';
import { ModalWithStyles } from '../modals/ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    flexGrow: 1,
  },
  pdfTextContainer: {
    position: 'relative',
    top: '64px',
  },
  pdfText: {
    color: 'black',
    fontSize: '0.65rem',
    maxWidth: '80px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  divider: {
    marginTop: theme.spacing(1),
    borderTop:
      theme.palette.mode === 'dark'
        ? 'solid 1px rgb(71,84,85)'
        : 'solid 1px rgb(194,192,195)',
    width: '100%',
  },
  mmsAttachmentContainer: {
    width: '100%',
    display: 'flex',
    // backgroundColor: 'black',
    flexWrap: 'wrap',
    maxHeight: '188px',
    overflow: 'scroll',
  },
  thumbnail: {
    height: '92px',
    width: '92px',
    border: '1px solid black',
    borderRadius: '8px',
    cursor: 'pointer',
    margin: theme.spacing(0, 0.5, 0.5),
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundColor: 'white',
  },
  thumbnail2: {
    height: '92px',
    borderRadius: '8px',
    width: '92px',
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(0, 0.5, 0.5),
    border: '1px solid black',
    cursor: 'pointer',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '36px',
    backgroundColor: 'white',
  },
  attachmentsHeader: {
    fontSize: '0.65rem',
    color: theme.palette.mode === 'dark' ? 'rgb(255,255,255)' : 'rgb(50,61,72)',
    fontFamily: 'Inter',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    display: 'flex',
  },
  chats: {
    minHeight: 200,
    maxHeight: 400,
    overflowY: 'scroll',
    '& li': {
      listStyle: 'none',
      padding: '5px 0',
      margin: '10px auto',
      fontSize: 12,
    },
    '& li.outgoing .message': {
      borderRight: '2px solid #F3565D',
      marginRight: 65,
      backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#fafafa',
      textAlign: 'right',
    },
    '& li.incoming .message': {
      textAlign: 'left',
      borderLeft: '2px solid #1BBC9B',
      marginLeft: 65,
      backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#fafafa',
    },
    '& li .message': {
      display: 'block',
      padding: 5,
      position: 'relative',
    },
    '& li.outgoing .message .arrow': {
      right: -8,
      borderLeft: '8px solid #F3565D',
    },
    '& li.incoming .message .arrow': {
      left: -8,
      width: 0,
      height: 0,
      borderRight: '8px solid #1BBC9B',
    },
    '& li.incoming .message .arrow, & li.outgoing .message .arrow': {
      display: 'block',
      position: 'absolute',
      top: 5,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
    },
    '& li .name': {
      color: '#3590c1',
      fontSize: 13,
      fontWeight: 400,
    },
    '& a': {
      textShadow: 'none',
      color: '#337ab7',
    },
    '& li.outgoing .datetime, & li.outgoing .name': {
      textAlign: 'right',
    },
    '& li .datetime': {
      fontSize: 13,
      fontWeight: 400,
      marginLeft: theme.spacing(1),
    },
    '& li .body': {
      display: 'block',
      wordWrap: 'break-word',
    },
  },
  message: {
    flexGrow: 1,
    paddingRight: 12.5,
  },
}));

interface IThreadedSms {
  /** Show modal state */
  show: boolean;
  /** Hide modal callback */
  onHide: () => void;
  /** userid sent to get threaded-sms */
  userid: string;
  /** outsidenumber sent to get threaded-sms */
  outsidenumber: string;
  /** Timeout between refetch */
  refetchTimeoutMs?: number;
}

export const saveFile = (url: string, fileName: string) => {
  // Get file name from url.
  const xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';
  xhr.onload = function () {
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(xhr.response); // xhr.response is a blob
    a.download = fileName; // Set the file name.
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(a.href);
    a.remove();
  };
  xhr.open('GET', url);
  xhr.send();
};

/**
 * Fetches latest thread (polling) and refreshes
 * the page every [refreshTimeoutMs] milliseconds
 * then scrolls to the bottom of dialog on new message
 */
export const ThreadedSms = ({
  show,
  onHide,
  userid,
  outsidenumber,
  refetchTimeoutMs = 15000,
}: IThreadedSms) => {
  const classes = useStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;

  // use requester
  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const [createEvent] = useCreateEventMutation();

  const { tier } = userAtom;
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const threadedSms = useRecoilValueLoadable<any>(
    makeAPIRequest({
      endpoint: endpointEnum.GET_THREADED_SMS,
      payloadJsonString: JSON.stringify({
        call: endpointEnum.GET_THREADED_SMS,
        number: outsidenumber,
        allSMS: true,
        startrow: 0,
        batchlength: 20,
        useBatch: true,
        timezone:
          businessData?.contents?.response?.data?.result?.[0]?.timezone_php,
        userid: userid,
      }),
    })
  );

  const threadedSmsCached = useRef<any>([]);

  threadedSmsCached.current = useMemo(
    () =>
      threadedSms.state === 'loading'
        ? threadedSmsCached.current
        : threadedSms.contents?.response?.data.previousMessages // can be empty string
        ? threadedSms.contents?.response?.data.previousMessages
        : [],
    [threadedSms]
  );

  const prevThreadedSmsCachedLast = usePrevious<any>(
    threadedSmsCached.current?.length
      ? threadedSmsCached.current?.[threadedSmsCached.current?.length - 1]
      : undefined
  );

  const setRefetchForThreadedSms = useSetRecoilState(
    refetchToggle(endpointEnum.GET_THREADED_SMS)
  );

  const handleSubmit = async (
    { userid, number, message }: any,
    { setSubmitting, setStatus, resetForm, error }: any
  ) => {
    const formData = new FormData();
    formData.append('businessid', String(userAtom.businessid));
    formData.append('businessId', String(userAtom.businessid));
    formData.append('userid', String(userAtom.userid));
    formData.append('userId', String(userAtom.userid));
    formData.append('apiKey', userAtom.apikey);
    formData.append('number', number);
    formData.append('message', encodeURIComponent(message));

    const newAccessToken = cookie.load('accessToken');
    const response: {
      data: {
        success: boolean;
        msg?: string;
        error?: string;
        errorMessage?: string;
      };
    } = await axios.post(
      `${process.env.VITE_APP_API_DOMAIN}/itn/sendmms`,
      formData,
      {
        headers: {
          Authorization: newAccessToken,
          // Accept: 'Accept: */*',
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    if (response?.data?.success) {
      const payload = {
        ...response.data,
        sentSms: true,
      };
      createEvent({
        auth: {
          apikey: userAtom.apikey,
        },
        event: {
          source: ELocation.Dash,
          target: ELocation.Powercall,
          type: EEventType.SmsSend,
          group: EEventGroup.Unknown,
          userId: userAtom.userid,
          businessId: userAtom.businessid,
          payload: JSON.stringify(payload),
        },
      });
      setRefetchForThreadedSms({});
      resetForm({
        userid,
        number: outsidenumber,
        message: '',
      });
    }
  };

  const messagesEndRef = useRef<HTMLLIElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // check timestamp between current latest messgae and previous latest message
  // scroll to bottom when change detected
  useEffect(() => {
    const lastMessage = threadedSmsCached.current?.length
      ? threadedSmsCached.current?.[threadedSmsCached.current?.length - 1]
      : undefined;

    // scroll to bottom if new message detected
    if (lastMessage?.created !== prevThreadedSmsCachedLast?.created) {
      // hackfix: set 100ms timeout due to fetch and render async
      setTimeout(scrollToBottom, 100);
    }
    // deps: only use threadedSms since
    // threadedSmsCached/prevThreadedSmsCachedLast
    // do not cause rerender
  }, [threadedSms]);

  const threadedSmsWithMMS = useMemo(() => {
    return threadedSmsCached?.current.length
      ? threadedSmsCached.current.map((thread: any) => {
          return {
            ...thread,
            multimedia: (thread.mediaurls?.length
              ? thread.mediaurls.split(',')
              : []
            )
              ?.filter((mms: string) => !!mms)
              .map((mms) => {
                return {
                  mms,
                  isPdf: mms?.slice(-4) === '.pdf',
                  filename: mms?.slice(mms?.lastIndexOf('/') + 1),
                };
              }),
          };
        })
      : [];
  }, [threadedSmsCached?.current]);

  // set 3 second interval timer to fetch threaded-sms
  // while component active
  useEffect(() => {
    const interval = setInterval(() => {
      setRefetchForThreadedSms({});
    }, refetchTimeoutMs);
    return () => clearInterval(interval);
  }, []);

  return tier === 'Basic' ? (
    <Redirect to="/history/call-history" />
  ) : (
    <Formik
      enableReinitialize={true}
      initialValues={{
        userid,
        number: outsidenumber,
        message: '',
      }}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <ModalWithStyles
          size="md"
          show={show}
          onHide={onHide}
          aria-labelledby="example-modal-sizes-title-lg"
          title="Messages"
          hideFooter={true}
          nonScroll={true}
        >
          <Paper square={true} elevation={0} className="p-0">
            {false && <ModalProgressBar />}
            <form
              className="form form-label-right"
              onSubmit={formik.handleSubmit}
              noValidate
            >
              <div className={`overlay overlay-block cursor-default`}>
                <main id="smshistory" className="threadedSMS">
                  <ul className={classes.chats}>
                    {threadedSmsWithMMS.length ? ( // previousMessage can be empty string
                      threadedSmsWithMMS
                        .map((thread: any) => {
                          const multimedia = thread.multimedia ?? [];
                          return (
                            <li
                              key={thread.externalid}
                              className={thread.direction}
                            >
                              <div className="message">
                                <span className="arrow"> </span>
                                <a href="#" className="name">
                                  {thread.direction === 'outgoing'
                                    ? 'you'
                                    : 'customer'}
                                </a>
                                <span className="datetime">
                                  {moment(thread.created).fromNow()}
                                </span>
                                <span className="body">{thread.message}</span>
                                {!!multimedia?.length && (
                                  <>
                                    <div
                                      style={{
                                        display: 'flex',
                                        margin: '4px 0px',
                                      }}
                                    >
                                      <div
                                        className={classes.divider}
                                        style={{ marginRight: '4px' }}
                                      />
                                      <div
                                        className={classes.attachmentsHeader}
                                      >
                                        {`Attachments (${multimedia?.length}):`}
                                      </div>
                                      <div
                                        className={classes.divider}
                                        style={{ marginLeft: '4px' }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        justifyContent:
                                          thread.direction === 'incoming'
                                            ? 'flex-start'
                                            : 'flex-end',
                                      }}
                                      className={classes.mmsAttachmentContainer}
                                    >
                                      {multimedia?.map(
                                        (
                                          mmsObj: {
                                            mms: string;
                                            filename: string;
                                            isPdf: boolean;
                                          },
                                          mmsIndex: number
                                        ) => {
                                          return (
                                            <Tooltip
                                              key={mmsIndex}
                                              title={mmsObj.filename}
                                            >
                                              <div
                                                onClick={() =>
                                                  saveFile(
                                                    mmsObj.mms,
                                                    mmsObj.filename
                                                  )
                                                }
                                              >
                                                {mmsObj.isPdf ? (
                                                  <div
                                                    className={
                                                      classes.thumbnail2
                                                    }
                                                    style={{
                                                      backgroundImage: `url(${
                                                        process.env.PUBLIC_URL +
                                                        '/media/logos/pdf.svg'
                                                      })`,
                                                    }}
                                                  >
                                                    <div
                                                      className={
                                                        classes.pdfTextContainer
                                                      }
                                                    >
                                                      <p
                                                        className={
                                                          classes.pdfText
                                                        }
                                                      >
                                                        {mmsObj.filename}
                                                      </p>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div
                                                    className={
                                                      classes.thumbnail
                                                    }
                                                    style={{
                                                      backgroundImage: `url("${mmsObj.mms}")`,
                                                    }}
                                                  />
                                                )}
                                              </div>
                                            </Tooltip>
                                          );
                                        }
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </li>
                          );
                        })
                        .reverse()
                    ) : threadedSms.state === 'loading' ? (
                      <li>
                        <div className="message">
                          <span className="body">Loading...</span>
                        </div>
                      </li>
                    ) : (
                      <li>
                        <div className="message">
                          <span className="body">No Messages</span>
                        </div>
                      </li>
                    )}
                    <li ref={messagesEndRef}></li>
                  </ul>
                </main>
              </div>
              <Modal.Footer>
                <FormikTextField
                  className={classes.message}
                  name="message"
                  formik={formik}
                  size="small"
                  variant="outlined"
                  disabled={mutation.isLoading}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={mutation.isLoading}
                  disableElevation
                >
                  Send
                </Button>
              </Modal.Footer>
            </form>
          </Paper>
        </ModalWithStyles>
      )}
    </Formik>
  );
};
